import { useCallback, useEffect, useState } from 'react';
import { useAdminApi, ApiError } from '../../Admin/shared/adminApi';

/**
 * Every headline arrives in the same envelope, so one tile component renders
 * all seven and the trend arithmetic is written once.
 *
 * `value: null` is load-bearing. It means the number could not be computed —
 * a failed query, an immature cohort, an untracked channel — and is never the
 * same claim as zero. Render it as "—", never as a decline.
 */
export interface Metric {
  value: number | null;
  prior: number | null;
  /** null when `prior` is 0: there is no percentage change from nothing. */
  delta_pct: number | null;
  unit: 'count' | 'percent';
  /** Percent metrics carry their fraction so the tile can show "4 of 10". */
  numerator?: number;
  denominator?: number;
  note?: string;
  series?: { period_start: string; value: number | null }[];
}

export interface VerifiedVisits extends Metric {}

export interface ActiveUsers extends Metric {
  /** Trailing 7 / 30 days, fixed regardless of the selected window. */
  wau: number;
  mau: number;
}

export interface Activation extends Metric {
  /** Signups too recent to have had their 7 days — reported, never scored. */
  maturing: number;
}

export interface Cohort {
  cohort_start: string;
  size: number;
  d7_pct: number | null;
  d30_pct: number | null;
}

export interface Retention {
  d7: Metric;
  d30: Metric;
  repeat_pct: Metric;
  cohorts: Cohort[];
}

export interface SocialGraph {
  avg_friends: Metric;
  pct_with_friend: Metric;
}

export interface DealVenues {
  venues: Metric;
  attributed_visits: Metric;
}

export interface Signups {
  total: Metric;
  by_channel: { channel: string; entered_via: string | null; count: number; prior: number }[];
  note: string;
}

/** A headline is null when its metric group failed — the rest still render. */
export interface Headlines {
  verified_visits: VerifiedVisits | null;
  active_users: ActiveUsers | null;
  activation: Activation | null;
  retention: Retention | null;
  social_graph: SocialGraph | null;
  deal_venues: DealVenues | null;
  signups: Signups | null;
}

export interface Scorecard {
  window: {
    days: number | 'all';
    all_time: boolean;
    from: string;
    to: string;
    prior_from: string;
    prior_to: string;
  };
  generated_at: string;
  headlines: Headlines;
}

/** 'all' has no comparable prior period, so every `prior` comes back null. */
export type ScorecardDays = 7 | 30 | 90 | 'all';

/**
 * The founder scorecard. Prod only — these numbers mean nothing anywhere else.
 *
 * One request returns all seven headlines with their prior periods, so the
 * window toggle refetches but nothing else does.
 */
export function useFounderScorecard(days: ScorecardDays) {
  const { request } = useAdminApi();
  const [data, setData] = useState<Scorecard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(
        await request<Scorecard>({
          env: 'prod',
          path: '/admin/analytics/scorecard',
          query: { days: String(days) },
        }),
      );
    } catch (e) {
      setData(null);
      setError(e instanceof ApiError ? e.message : 'Could not load the scorecard');
    } finally {
      setLoading(false);
    }
  }, [request, days]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, reload: load };
}
